import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Ban, Loader2, ShieldOff, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const REASONS = [
  { code: 'excessive_emails', label: 'Too many emails / invitations' },
  { code: 'poor_experience', label: 'Poor experience' },
  { code: 'safety_concern', label: 'Safety concern' },
  { code: 'other', label: 'Other' },
];

export default function BlockedClients() {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [blockable, setBlockable] = useState([]);
  const [picked, setPicked] = useState(null);
  const [reasonCode, setReasonCode] = useState('excessive_emails');
  const [reasonText, setReasonText] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmActive, setConfirmActive] = useState(null); // active bookings warning payload

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/talent/blocked-clients');
      setBlocks(r.data.blocks || []);
    } catch {
      toast.error('Could not load blocked clients');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    setPicked(null); setReasonCode('excessive_emails'); setReasonText(''); setConfirmActive(null);
    setAddOpen(true);
    try {
      const r = await api.get('/talent/blocked-clients/blockable');
      setBlockable(r.data.clients || []);
    } catch { setBlockable([]); }
  };

  const submitBlock = async (acknowledged = false) => {
    if (!picked) { toast.error('Pick a client to block'); return; }
    setBusy(true);
    try {
      const r = await api.post('/talent/blocked-clients', {
        client_id: picked.client_id,
        reason_code: reasonCode,
        reason_text: reasonText.trim() || null,
        acknowledged_active_bookings: acknowledged,
      });
      if (r.data.requires_confirmation) {
        setConfirmActive(r.data);
        setBusy(false);
        return;
      }
      toast.success(`Blocked ${picked.company_name}. You won't hear from them again.`);
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not block client');
    } finally { setBusy(false); }
  };

  const unblock = async (b) => {
    if (!window.confirm(`Unblock ${b.company_name}? You'll start receiving their gigs and invitations again.`)) return;
    try {
      await api.delete(`/talent/blocked-clients/${b.client_id}`);
      toast.success(`Unblocked ${b.company_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unblock');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blocked-clients-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/talent/settings')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>

        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldOff className="w-6 h-6 text-rose-500" /> Blocked Clients
          </h1>
          <Button onClick={openAdd} className="bg-rose-600 hover:bg-rose-700" data-testid="add-block-btn">
            <Plus className="w-4 h-4 mr-1" /> Block a client
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Blocking is private — the client is never told. You'll stop getting their gig invitations,
          emails, texts, push notifications and gig recommendations. Your existing bookings and pay are unaffected.
        </p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        ) : blocks.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400 text-sm" data-testid="no-blocks">
            You haven't blocked any clients. Everything's open.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {blocks.map((b) => (
              <Card key={b.id} data-testid={`block-row-${b.client_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 flex items-center gap-2 truncate">
                      <Ban className="w-4 h-4 text-rose-500 shrink-0" /> {b.company_name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Blocked {new Date(b.created_at).toLocaleDateString()}
                      {b.reason_text ? ` · ${b.reason_text}` : ''}
                    </div>
                    {b.active_bookings_review_required && (
                      <Badge className="mt-1 bg-amber-100 text-amber-700 border-0 text-[11px]">
                        Active booking under admin review
                      </Badge>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => unblock(b)} data-testid={`unblock-btn-${b.client_id}`}>
                    Unblock
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-w-md" data-testid="add-block-modal">
          <DialogHeader><DialogTitle>Block a client</DialogTitle></DialogHeader>

          {confirmActive ? (
            <div className="space-y-3" data-testid="active-booking-warning">
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>{confirmActive.message}</div>
              </div>
              <div className="text-xs text-slate-500">
                We won't cancel these — your admin team reviews them. Contact support if you need out of a booking.
              </div>
              <ul className="text-xs text-slate-600 space-y-1">
                {(confirmActive.active_bookings || []).map((a) => (
                  <li key={a.id}>• {new Date(a.shift_date).toLocaleDateString()} — {a.event_title || a.venue_name}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Client</Label>
                {blockable.length === 0 ? (
                  <div className="text-sm text-slate-400 py-2">No clients you've worked with or been invited by yet.</div>
                ) : (
                  <div className="border border-slate-200 rounded-lg max-h-52 overflow-y-auto mt-1">
                    {blockable.map((c) => (
                      <button
                        key={c.client_id}
                        onClick={() => setPicked(c)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${picked?.client_id === c.client_id ? 'bg-rose-50' : ''}`}
                        data-testid={`blockable-${c.client_id}`}
                      >
                        {c.company_name}
                        {picked?.client_id === c.client_id && <Badge className="bg-rose-600 text-white border-0 text-[10px]">Selected</Badge>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {REASONS.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => setReasonCode(r.code)}
                      className={`text-xs rounded-lg border px-2 py-2 ${reasonCode === r.code ? 'border-rose-400 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-600'}`}
                      data-testid={`reason-${r.code}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Textarea rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)} data-testid="block-reason-text" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="block-cancel-btn">Cancel</Button>
            {confirmActive ? (
              <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={() => submitBlock(true)} data-testid="block-confirm-anyway-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Block anyway
              </Button>
            ) : (
              <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy || !picked} onClick={() => submitBlock(false)} data-testid="block-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Block client
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
